import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { cloneDeep, isEqual, shuffle } from 'lodash';
import numbro from 'numbro';
import { shallow } from 'zustand/shallow';

import {
  LoadingSpinner,
  MemoryBoostInfoModal,
  RewardStationCellState,
  StationCellProps,
  StationCellType,
  VideoPlayer,
} from '../../components/Molecules';
import {
  AboutJourneySheetPopup,
  GameTypeHelpPopup,
  GameTypeHelpType,
  JourneyOptionsPopup,
  stringToGameTypeHelpType,
} from '../../components/Popups';
import { DeleteJourneyPopup } from '../../components/Popups/DeleteJourneyPopup';
import { RenameJourneyPopup } from '../../components/Popups/RenameJourneyPopup';
import { AlbumPathListTemplate } from '../../components/Templates';
import {
  StationEmptyItemNo,
  StationGameItemNo,
  StationGoalItemNo,
  StationMiniChallengeItemNo,
  StationRewardItemNo,
  StationSlideshowItemNo,
  StationStartItemNo,
  StationSuperChallengeItemNo,
  StationTutorGameItemNo,
  StationType,
} from '../../constants/Games/gameStation';
import { AppToastErrorOption, AppToastInfoOption } from '../../constants/toast';
import {
  getWebPageLink,
  WebPageLink,
  WebPageType,
} from '../../constants/webPageType';
import { usePopup, useShareData } from '../../context';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import usePrivateAttachment from '../../hooks/usePrivateAttachments';
import useRedirectHome from '../../hooks/useRedirectHome';
import { PartKey, VideoVersion } from '../../models/Base';
import { GameListsType } from '../../models/SessionStore';
import { ProfileExample } from '../../models/UserStore';
import { useGameStore, useGameStoreListField } from '../../stores/GameStore';
import {
  useRecordStore,
  useRecordStoreListField,
  useRecordStoreMapField,
} from '../../stores/RecordStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { getTimeString } from '../../utils/date';
import { computeReward } from '../../utils/record';

const { fetchSpace, fetchAlbum, seeSpace, updateAlbum, deleteAlbum } =
  useSpaceStore.getState();
const { fetchGameCollectionsByIds, fetchGame, fetchGameStatistics } =
  useGameStore.getState();
const { fetchAlbumSummaries } = useRecordStore.getState();

type PathStationCellProps = StationCellProps & {
  isCompleted?: boolean;
  isForChallengeGame?: boolean;
};

enum PopupState {
  About = 'about',
  MemoryBoost = 'memoryBoost',
}

type State = {
  isLoading: boolean;
  isFinishedFetchSpace: boolean;
  isFinishedFetchAlbum: boolean;
  popupState: PopupState | undefined;
  blinkingStationIndex: number | undefined;
  isStationsUpdated: boolean;
  isForChallengeGameOnly: boolean;
  renameJourneyName: string;
  errorMessage: string | undefined;
};

const AlbumPathListScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { setSharedData } = useShareData();
  const { classroomId, gameSetId } = useParams();
  const { showPopup, hidePopup } = usePopup();

  const navigationState = location.state as {
    withBackState: boolean;
  } | null;

  const [state, setState] = useState<State>({
    isLoading: true,
    isFinishedFetchSpace: false,
    isFinishedFetchAlbum: false,
    popupState: undefined,
    blinkingStationIndex: undefined,
    isStationsUpdated: false,
    isForChallengeGameOnly: false,
    renameJourneyName: '',
    errorMessage: undefined,
  });

  const activeUserId = useSessionStore((store) => store.activeUserId);

  // const userAccount = useUserStore((store) =>
  //   store.getAccount(activeUserId ?? ''),
  // );
  const userProfile = useUserStore(
    (store) => store.getProfile(activeUserId ?? '') ?? ProfileExample,
  );

  const currentNow = useMemo(() => new Date(), []);

  // useRedirectEntry();

  // For handle safari cached from back-forward cache
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // MARK: Get Space
  const spaceListFieldName = `${classroomId}`;
  useSpaceStoreListField('spaces', spaceListFieldName);

  useEffect(() => {
    if (classroomId) {
      fetchSpace(spaceListFieldName, classroomId)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, isFinishedFetchSpace: true }));
        });
    }
  }, [classroomId, spaceListFieldName]);

  const { classroom, classroomMembership } = useSpaceStore((store) => {
    const space = store.getSpace(classroomId ?? '');
    const membership = store.getActiveUserMembership(classroomId ?? '');
    return {
      classroom: space,
      classroomMembership: membership,
    };
  }, shallow);

  useRedirectHome(state.isFinishedFetchSpace && !classroom);

  // TODO: need to be async?

  useEffect(() => {
    if (classroomId) {
      seeSpace(classroomId).catch(console.error);
    }
  }, [classroomId]);

  // were duplicated with seeSpace
  // const visitClassroom = useCallback(() => {
  //   changeUserSpaceMembership(classroomId ?? '', {
  //     operation: SpaceMembershipOperation.Visit,
  //   }).catch(console.error);
  // }, [classroomId]);

  // useEffect(() => {
  //   if (classroom && classroomMembership !== undefined) {
  //     if (classroomMembership === null && classroom.type !== SpaceType.Home) {
  //       visitClassroom();
  //     }
  //   }
  // }, [classroom, classroomMembership, visitClassroom]);

  // MARK: Get Album
  const albumListFieldName = `${classroomId}_${gameSetId}`;
  useSpaceStoreListField('albums', albumListFieldName);

  useEffect(() => {
    if (classroom && gameSetId) {
      fetchAlbum(
        {
          fieldName: albumListFieldName,
          classroomId: classroom.classroomId,
          gameSetId,
        },
        { headers: { 'cache-control': 'no-cache' } },
      )
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, isFinishedFetchAlbum: true }));
        });
    }
  }, [albumListFieldName, classroom, gameSetId]);

  const album = useSpaceStore(
    (store) => store.getAlbum(gameSetId ?? ''),
    shallow,
  );

  const albumInfo =
    album?.i18nGameSetInfo.resources[album?.i18nGameSetInfo.defaultLocale];
  const albumName = albumInfo?.name;

  useRedirectHome(state.isFinishedFetchAlbum && !album);

  // MARK: Get GameCollection
  const gameCollectionFieldName = `album_${album?.gameSetId}`;
  useGameStoreListField('gameCollections', gameCollectionFieldName);

  useEffect(() => {
    if (album?.content?.gameCollectionIds && album?.gameSetId) {
      const gameCollectionIds = album?.content?.gameCollectionIds;
      if (gameCollectionIds) {
        fetchGameCollectionsByIds({
          fieldName: gameCollectionFieldName,
          gameCollectionIds: gameCollectionIds,
        }).catch(console.error);
      }
    }
  }, [
    album?.content?.gameCollectionIds,
    album?.gameSetId,
    gameCollectionFieldName,
  ]);

  const gameItems = useGameStore((store) => {
    const gameCollections = (album?.content?.gameCollectionIds ?? []).map(
      (id) => store.getGameCollection(id),
    );
    if (
      gameCollections.some((gameCollection) => gameCollection === undefined)
    ) {
      // only return data if all game collection loaded
      return undefined;
    }

    return gameCollections.flatMap(
      (gameCollection) =>
        gameCollection?.getPartItemArray((item) => item.game) ?? [],
    );
  }, arrayEqual(shallow));

  // MARK: Get Games And GameStatistics
  const gameCollectionIdsJoin = album?.content?.gameCollectionIds.join('_');
  const gamesFieldName = `${gameCollectionIdsJoin}`;
  const gameStatisticsFieldName = `${gameSetId}`;
  useGameStoreListField('games', gamesFieldName);
  useGameStoreListField('gameStatistics', gameStatisticsFieldName);

  useEffect(() => {
    if (gameItems && album) {
      gameItems.forEach((item) => {
        const gameCollectionId = item.gameCollectionId;
        const gameId = item.game?.gameId;
        const hash = item.game?.hash;
        if (gameId && hash) {
          fetchGame({
            fieldName: gameCollectionIdsJoin ?? '',
            gameCollectionId,
            gameId,
            hash,
          })
            .then(() => {
              if (activeUserId === null) {
                setTimeout(() => {
                  setState((s) => ({
                    ...s,
                    isStationsUpdated: true,
                    isLoading: false,
                  }));
                }, 500);
              }
            })
            .catch(console.error);
          fetchGameStatistics(
            { fieldName: album.gameSetId, gameCollectionId, gameId, hash },
            { headers: { 'cache-control': 'no-cache' } },
          ).catch(console.error);
        }
      });
    }
  }, [activeUserId, album, gameCollectionIdsJoin, gameItems]);

  const allGameStatistics = useGameStore(
    (store) => store.getGameStatisticsList(gameSetId ?? ''),
    arrayEqual(shallow),
  );

  const games = useGameStore((store) => {
    return gameItems?.map((gameItem) =>
      store.getGame(gameItem?.game?.gameId ?? ''),
    );
  }, arrayEqual(shallow));

  // MARK: Get Album Summary
  const albumSummaryFieldName = `album_${gameSetId}`;
  useRecordStoreMapField('albumSummaries', albumSummaryFieldName);

  useEffect(() => {
    if (album?.gameSetId && activeUserId !== null) {
      fetchAlbumSummaries(
        { gameSetIds: [album?.gameSetId] },
        { headers: { 'cache-control': 'no-cache' } },
      ).catch(console.error);
    }
  }, [activeUserId, album?.gameSetId]);

  const albumSummary = useRecordStore((store) =>
    store.getAlbumSummary(gameSetId ?? '', activeUserId ?? ''),
  );

  useEffect(() => {
    if (albumSummary) {
      const showGameForChallengeOnly =
        albumSummary.path?.showGameForChallengeOnly;
      if (showGameForChallengeOnly !== undefined) {
        setState((s) => ({
          ...s,
          isForChallengeGameOnly: showGameForChallengeOnly,
        }));
      }
    }
  }, [albumSummary]);

  // MARK: Get GameRecord
  const gameRecordFieldName = `${classroomId}_${gameSetId}`;
  const gameRecordField = useRecordStoreListField(
    'gameRecords',
    gameRecordFieldName,
  );

  const fetchGameRecords = useCallback<FetchListFn>(
    (cursor) =>
      useRecordStore.getState().fetchGameRecords(
        {
          fieldName: gameRecordFieldName,
          classroomId: classroomId ?? '',
          playedBy: activeUserId ?? '',
          gameSetId: gameSetId ?? '',
          hash: currentNow.toTimeString(),
        },
        cursor,
        { headers: { 'cache-control': 'no-cache' } },
      ),
    [activeUserId, classroomId, currentNow, gameRecordFieldName, gameSetId],
  );

  const { fetchAll: fetchAllGameRecords } = useFetchList(
    gameRecordField,
    fetchGameRecords,
  );

  useEffect(() => {
    if (gameItems && activeUserId !== null) {
      setState((s) => ({ ...s, isStationsUpdated: false, isLoading: true }));
      fetchAllGameRecords()
        .then(() => {
          setTimeout(() => {
            setState((s) => ({
              ...s,
              isStationsUpdated: true,
              isLoading: false,
            }));
          }, 500);
        })
        .catch((error) => {
          console.error(error);
          setState((s) => ({ ...s, isStationsUpdated: true }));
        });
    }
  }, [activeUserId, fetchAllGameRecords, gameItems]);

  const allGameRecords = useRecordStore((store) => {
    return activeUserId === null
      ? []
      : store.getGameRecords(gameRecordFieldName);
  }, arrayEqual(shallow));

  // MARK: Get Challenge Record
  const challengeRecordFieldName = `${classroomId}_${gameSetId}`;
  const challengeRecordField = useRecordStoreListField(
    'challengeRecords',
    challengeRecordFieldName,
  );

  const fetchChallengeRecords = useCallback<FetchListFn>(
    (cursor) =>
      useRecordStore.getState().fetchChallengeRecords(
        {
          fieldName: challengeRecordFieldName,
          classroomId: classroomId ?? '',
          gameSetId: gameSetId ?? '',
          hash: currentNow.toTimeString(),
        },
        cursor,
        { headers: { 'cache-control': 'no-cache' } },
      ),
    [challengeRecordFieldName, classroomId, currentNow, gameSetId],
  );

  const { fetchAll: fetchAllChallengeRecords } = useFetchList(
    challengeRecordField,
    fetchChallengeRecords,
  );

  useEffect(() => {
    if (gameItems && activeUserId !== null) {
      fetchAllChallengeRecords().catch(console.error);
    }
  }, [activeUserId, fetchAllChallengeRecords, gameItems]);

  const allChallengeRecords = useRecordStore((store) => {
    return activeUserId === null
      ? []
      : store.getChallengeRecords(challengeRecordFieldName);
  }, arrayEqual(shallow));

  // MARK: Get Start Video
  const startVideoUrl = usePrivateAttachment(
    album?.content?.startVideo,
    VideoVersion.WebOptimized,
  );
  const startVideoThumbnailUrl = usePrivateAttachment(
    album?.content?.startVideo,
    VideoVersion.thumbnail,
  );

  const aspectRatioOfVideo = album?.content?.startVideo?.aspectRatio;

  const videoAspectRatio = aspectRatioOfVideo
    ? `${aspectRatioOfVideo}/1`
    : '16/9';

  // if (aspectRatioOfVideo) {
  // const newAspectRatio = window.innerWidth / window.innerHeight;
  // videoAspectRatio = `${aspectRatioOfVideo}/1`;

  // if (aspectRatioOfVideo > newAspectRatio) {
  //   videoMaxWidth = '100%';
  //   videoMaxHeight = `${(newAspectRatio / aspectRatioOfVideo) * 100}%`;
  // } else {
  //   videoMaxHeight = '100%';
  //   videoMaxWidth = `${(aspectRatioOfVideo / newAspectRatio) * 100}%`;
  // }
  // }

  const onClickVideo = useCallback(() => {
    showPopup(
      <VideoPlayer
        url={startVideoUrl}
        thumbnailUrl={startVideoThumbnailUrl}
        playing={true}
        muted={false}
        onEnded={() => {
          if (classroom && album) {
            useRecordStore
              .getState()
              .viewStartVideo(classroom.classroomId, album.gameSetId)
              .catch(() => {
                console.error();
              });
          }
        }}
      />,
      {
        maxWidth: '100%',
        maxHeight: '100%',
        aspectRatio: videoAspectRatio,
        withCross: true,
      },
    );
  }, [
    album,
    classroom,
    showPopup,
    startVideoThumbnailUrl,
    startVideoUrl,
    videoAspectRatio,
  ]);

  const onClickInfo = useCallback(
    (type: GameTypeHelpType) => {
      showPopup(<GameTypeHelpPopup type={type} onPrimary={hidePopup} />);
    },
    [hidePopup, showPopup],
  );

  const onClickAboutJourney = useCallback(() => {
    if (!album) {
      return;
    }
    setState((s) => ({ ...s, popupState: PopupState.About }));
  }, [album]);

  const onRenameJourney = useCallback(
    (name: string) => {
      // TODO: handle error
      if (name.length <= 0 || name.length > 50) {
        return;
      }

      hidePopup();
      if (classroom && album) {
        setState((s) => ({ ...s, isLoading: true }));

        const i18nGameSetInfo = cloneDeep(album.i18nGameSetInfo);
        i18nGameSetInfo.resources[i18nGameSetInfo.defaultLocale]!.name = name;

        updateAlbum(classroom.classroomId, album.gameSetId, {
          i18nGameSetInfo,
        })
          .catch(console.error)
          .finally(() => {
            setState((s) => ({ ...s, isLoading: false }));
          });
      }
    },
    [album, classroom, hidePopup],
  );

  const onClickRenameJourney = useCallback(() => {
    showPopup(
      <RenameJourneyPopup
        originalName={albumName ?? ''}
        onRenameJourney={onRenameJourney}
        onCancel={hidePopup}
      />,
    );
  }, [albumName, hidePopup, onRenameJourney, showPopup]);

  const onDeleteJourney = useCallback(() => {
    hidePopup();
    if (classroom && album) {
      setState((s) => ({ ...s, isLoading: true }));

      deleteAlbum(classroom.classroomId, album.gameSetId)
        .then((isDeleted) => {
          setState((s) => ({ ...s, isLoading: false }));
          if (isDeleted) {
            toast(t('Journey is deleted.'), AppToastInfoOption);
            navigate('/club/journey');
          } else {
            toast(t('There is something wrong.'), AppToastErrorOption);
          }
        })
        .catch(console.error);
    }
  }, [album, classroom, hidePopup, navigate, t]);

  const onClickDeleteJourney = useCallback(() => {
    showPopup(
      <DeleteJourneyPopup onPrimary={onDeleteJourney} onCancel={hidePopup} />,
    );
  }, [hidePopup, onDeleteJourney, showPopup]);

  const onClickAlbumOptions = useCallback(() => {
    showPopup(
      <JourneyOptionsPopup
        onRenameJourney={onClickRenameJourney}
        onDeleteJourney={onClickDeleteJourney}
        onCancel={hidePopup}
      />,
    );
  }, [hidePopup, onClickRenameJourney, onClickDeleteJourney, showPopup]);

  const onClickClassroom = useCallback(() => {
    if (navigationState?.withBackState) {
      navigate(-1);
    } else if (classroomId === activeUserId) {
      navigate('/club/journey');
    } else {
      navigate(`/club/${classroomId}`);
    }

    window.scrollTo(0, 0);
  }, [activeUserId, classroomId, navigate, navigationState?.withBackState]);

  const hashForPlayCount = useMemo(() => {
    return (playCount: number): number => {
      // murmurHash3 implementation
      function murmurhash3(key: number, seed = 0) {
        let k = Math.imul(key, 0xcc9e2d51);
        k = (k << 15) | (k >>> 17);
        k = Math.imul(k, 0x1b873593);

        let h = seed;
        h ^= k;
        h = (h << 13) | (h >>> 19);
        h = Math.imul(h, 5) + 0xe6546b64;

        h ^= 4;
        h ^= h >>> 16;
        h = Math.imul(h, 0x85ebca6b);
        h ^= h >>> 13;
        h = Math.imul(h, 0xc2b2ae35);
        h ^= h >>> 16;

        return Math.abs(h);
      }

      const pathValue = Number(album?.content?.path[0]?.stations.length) ?? 5;
      const hash = murmurhash3(playCount, pathValue);
      const result = 70 + (hash % 11);

      return result;
    };
  }, [album?.content?.path]);

  const onSharePath = useCallback(() => {
    const pageUrl = window.location.href;

    const updatedUrl = new URL(pageUrl);

    if (navigator.share) {
      navigator.share({ url: updatedUrl.toString() }).catch((err) => {
        console.error('Failed to share the URL: ', err);
      });
    } else {
      navigator.clipboard
        .writeText(updatedUrl.toString())
        .then(() => {
          toast(t('Link is copied!'), {
            ...AppToastInfoOption,
            id: 'clipboard',
          });
        })
        .catch((err) => {
          console.error('Failed to copy the URL: ', err);
        });
    }
  }, [t]);

  const onShareGame = useCallback(
    (link: string) => {
      const pageUrl = window.location.href;

      const updatedUrl = new URL(pageUrl);
      updatedUrl.pathname = `${link}`;
      const finalUrl = decodeURIComponent(updatedUrl.toString()); // prevent ? => %3F
      if (navigator.share) {
        navigator.share({ url: finalUrl }).catch((err) => {
          console.error('Failed to share the URL: ', err);
        });
      } else {
        navigator.clipboard
          .writeText(finalUrl)
          .then(() => {
            toast(t('Link is copied!'), {
              ...AppToastInfoOption,
              id: 'clipboard',
            });
          })
          .catch((err) => {
            console.error('Failed to copy the URL: ', err);
          });
      }
    },
    [t],
  );

  // MARK: Get Station Data
  const stationsData = useMemo<PathStationCellProps[]>(() => {
    if (album && classroom && userProfile && games) {
      const stations = album.content?.path
        .flatMap((path) => path.stations)
        .filter(
          (station) =>
            station.type === StationType.Start ||
            station.type === StationType.Game ||
            station.type === StationType.AssistedGame ||
            station.type === StationType.PathChallenge ||
            station.type === StationType.SuperChallenge ||
            // station.type === StationType.Reward ||
            station.type === StationType.Goal,
        );
      const _stationStyle = album.content?.path[0]?.style;

      const stationStyle = _stationStyle
        ? {
            background: _stationStyle?.background,
            decorations: _stationStyle?.decorations,
            stations: {
              reward: StationRewardItemNo.s1,
              game: StationGameItemNo.s0,
              goal: StationGoalItemNo.s0,
              start: StationStartItemNo.s0,
              slideshow: StationSlideshowItemNo.s1,
              assistedGame: StationTutorGameItemNo.s0,
              miniChallenge: StationMiniChallengeItemNo.s0,
              empty: StationEmptyItemNo.s1,
              superChallenge: StationSuperChallengeItemNo.s0,
            },
          }
        : undefined;
      let currentGameIndex = 0;
      let currentStandardGameIndex = 0;
      let currentAssistedGameIndex = 0;
      let challengeGameIndex = 0;
      if (stations) {
        let tempGameIndex = 0;
        let tempAssistedGameIndex = 0;
        let previousStationCompleted = true;
        let tempChallengeIndex = 0;
        let lastRecordIndex = 0;

        // get the newest station index which with record
        stations.forEach((station, index) => {
          if (
            station.type === StationType.Game ||
            station.type === StationType.AssistedGame
          ) {
            const currentIndex =
              station.type === StationType.Game
                ? tempGameIndex++
                : tempAssistedGameIndex++;
            const gameItem = gameItems?.[currentIndex];
            const isAssisted = station.type === StationType.AssistedGame;

            const hasPassedStation = allGameRecords.some(
              (record) =>
                record.gameId === gameItem?.game?.gameId &&
                record.isAssisted === isAssisted &&
                record.canPassStation,
            );
            if (hasPassedStation) {
              lastRecordIndex = index;
            }
          }

          if (station.type === StationType.PathChallenge) {
            // const currentIndex = challengeGameIndex++;
            const partId: PartKey = String(tempChallengeIndex).padStart(
              2,
              '0',
            ) as PartKey;
            const challengeGameRecords = allChallengeRecords.filter(
              (record) => record.type === `pathChallenge_${partId}`,
            );
            tempChallengeIndex += 1;
            const canPassStation = challengeGameRecords?.find(
              (record) => record.canPassStation,
            );
            if (canPassStation) {
              lastRecordIndex = index;
            }
          }

          if (station.type === StationType.SuperChallenge) {
            // const currentIndex = challengeGameIndex++;
            const challengeGameRecords = allChallengeRecords.filter(
              (record) => record.type === `superChallenge`,
            );
            const canPassStation = challengeGameRecords?.find(
              (record) => record.canPassStation,
            );
            if (canPassStation) {
              lastRecordIndex = index;
            }
          }
        });

        // rest tempIndex
        tempGameIndex = 0;
        tempAssistedGameIndex = 0;

        // get the newest station index
        const _newestUnlockedIndex = stations.findIndex((station) => {
          const isUnlocked =
            // (albumSummary?.path?.unlockedStations ?? 0) >= index ||
            previousStationCompleted;

          // console.log(station.type);

          if (
            station.type === StationType.Game ||
            station.type === StationType.AssistedGame
          ) {
            const currentIndex =
              station.type === StationType.Game
                ? tempGameIndex++
                : tempAssistedGameIndex++;
            const gameItem = gameItems?.[currentIndex];
            const isAssisted = station.type === StationType.AssistedGame;

            const hasPassedStation = allGameRecords.some(
              (record) =>
                record.gameId === gameItem?.game?.gameId &&
                record.isAssisted === isAssisted &&
                record.canPassStation,
            );

            previousStationCompleted = hasPassedStation;
            return isUnlocked && !hasPassedStation;
          }

          if (station.type === StationType.PathChallenge) {
            // const currentIndex = challengeGameIndex++;
            const partId: PartKey = String(tempChallengeIndex).padStart(
              2,
              '0',
            ) as PartKey;
            const challengeGameRecords = allChallengeRecords.filter(
              (record) => record.type === `pathChallenge_${partId}`,
            );
            tempChallengeIndex += 1;
            const canPassStation = challengeGameRecords?.find(
              (record) => record.canPassStation,
            );
            previousStationCompleted =
              canPassStation !== undefined ? true : false;
            return isUnlocked && !previousStationCompleted;
          }

          if (station.type === StationType.SuperChallenge) {
            // const currentIndex = challengeGameIndex++;
            const challengeGameRecords = allChallengeRecords.filter(
              (record) => record.type === `superChallenge`,
            );
            const canPassStation = challengeGameRecords?.find(
              (record) => record.canPassStation,
            );
            previousStationCompleted =
              canPassStation !== undefined ? true : false;
            return isUnlocked && !previousStationCompleted;
          }

          if (station.type === StationType.Goal) {
            return true;
          }

          if (station.type === StationType.Start) {
            const startVideoViewed =
              !album?.content?.startVideo ||
              !!albumSummary?.path?.viewedStartVideoAt;

            previousStationCompleted = startVideoViewed;
            return !previousStationCompleted;
          }
          return false;
        });

        const newestUnlockedIndex = Math.max(
          _newestUnlockedIndex,
          lastRecordIndex + 1,
        );

        const previousStationCompletedForChallenge = true;
        let tempGameIndexForChallenge = 0;
        let tempChallengeIndexForChallenge = 0;
        const newestUnlockedIndexForChallenge = stations.findIndex(
          (station) => {
            const isUnlocked =
              // (albumSummary?.path?.unlockedStations ?? 0) >= index ||
              previousStationCompletedForChallenge;

            if (station.type === StationType.Game) {
              const currentIndex = tempGameIndexForChallenge++;
              const gameItem = gameItems?.[currentIndex];

              const isStandardForChallenge =
                gameItem?.game?.isForChallenge ?? false;

              if (!isStandardForChallenge) {
                return false;
              }

              const hasPassedStation = allGameRecords.some(
                (record) =>
                  record.gameId === gameItem?.game?.gameId &&
                  record.canPassStation,
              );

              previousStationCompleted = hasPassedStation;
              return isUnlocked && !hasPassedStation;
            }

            if (station.type === StationType.PathChallenge) {
              // const currentIndex = challengeGameIndex++;
              const partId: PartKey = String(
                tempChallengeIndexForChallenge,
              ).padStart(2, '0') as PartKey;
              const challengeGameRecords = allChallengeRecords.filter(
                (record) => record.type === `pathChallenge_${partId}`,
              );
              tempChallengeIndexForChallenge += 1;
              const canPassStation = challengeGameRecords?.find(
                (record) => record.canPassStation,
              );
              previousStationCompleted =
                canPassStation !== undefined ? true : false;
              return isUnlocked && !previousStationCompleted;
            }
            if (station.type === StationType.SuperChallenge) {
              // const currentIndex = challengeGameIndex++;
              const challengeGameRecords = allChallengeRecords.filter(
                (record) => record.type === `superChallenge`,
              );
              const canPassStation = challengeGameRecords?.find(
                (record) => record.canPassStation,
              );
              previousStationCompleted =
                canPassStation !== undefined ? true : false;
              return isUnlocked && !previousStationCompleted;
            }
            if (station.type === StationType.Goal) {
              return true;
            }
            if (station.type === StationType.Start) {
              const startVideoViewed =
                !album?.content?.startVideo ||
                !!albumSummary?.path?.viewedStartVideoAt;

              previousStationCompleted = startVideoViewed;
              return !previousStationCompleted;
            }
            return false;
          },
        );

        let _indexForChallenge = 0;

        return stations.map((station, index) => {
          const name =
            station?.i18nStationInfo?.resources[
              station?.i18nStationInfo?.defaultLocale
            ]?.name;
          const hasViewedVideo =
            !album?.content?.startVideo ||
            !!albumSummary?.path?.viewedStartVideoAt;
          // const isUnlocked =
          //   hasViewedVideo &&
          //   (albumSummary?.path?.unlockedStations ?? 0) >= index;
          switch (station.type) {
            case StationType.Start:
              return {
                type: StationCellType.Start,
                props: {
                  album: album,
                  albumClassroom: classroom,
                  stationId:
                    stationStyle?.stations.start ?? StationStartItemNo.s1,
                  name: name,
                  startVideoThumbnailUrl: startVideoThumbnailUrl,
                  isUnlocked: true,
                  classroomOwnerProfile: userProfile,
                  userProfile: userProfile,
                  onClickPlay: onClickAboutJourney,
                  onClickVideo: onClickVideo,
                },
                isCompleted: hasViewedVideo,
                isForChallengeGame: false,
              };
            case StationType.Game: {
              const gameItem = gameItems?.[currentGameIndex];
              const game = games?.find(
                (g) => g?.gameId === gameItem?.game?.gameId,
              );
              const gameStatistics = allGameStatistics.find(
                (stats) => stats.gameId === gameItem?.game?.gameId,
              );
              const gameRecords = allGameRecords.filter(
                (record) =>
                  record.gameId === gameItem?.game?.gameId && record.isNormal,
              );
              const lastGameRecord = gameRecords.find(
                (record) => record.completed,
              );
              const remainingReward = computeReward(
                classroomMembership?.isAtLeastMember,
                gameItem?.gameReward ?? {},
                false,
                5,
                gameRecords?.map((record) => record.reward) ?? [],
                1,
                gameItem?.game?.answerSlots,
              ).reward;
              const canPassStation = gameRecords?.find(
                (record) => record.canPassStation,
              );
              currentGameIndex = currentGameIndex + 1;

              if (
                // gameItem?.icon?.tag !== 'syllable' &&
                // gameItem?.icon?.tag !== 'fingerWritingStrokeByComponent' &&
                gameItem?.game?.isForChallenge
              ) {
                currentStandardGameIndex = currentStandardGameIndex + 1;
              }
              const prefixCode =
                // gameItem?.icon?.tag === 'syllable' ||
                // gameItem?.icon?.tag === 'fingerWritingStrokeByComponent'
                gameItem?.game?.isForChallenge
                  ? `P${currentStandardGameIndex}`
                  : `P${currentStandardGameIndex + 1}.1`;

              if (gameItem?.game?.isForChallenge) {
                _indexForChallenge++;
              }
              const indexForChallenge = _indexForChallenge;
              if (game) {
                return {
                  type: StationCellType.Game,
                  props: {
                    game: game,
                    stationId:
                      stationStyle?.stations.game ?? StationGameItemNo.s1,
                    name: name,
                    index: index,
                    iconKey: gameItem?.icon?.tag ?? '',
                    prefixCode: prefixCode,
                    playCount: gameStatistics?.playCount ?? 0,
                    averageScore: gameStatistics?.averageScore ?? 0,
                    userProfile: userProfile,
                    remainingBreadReward: remainingReward.breadSlice,
                    recordScore: lastGameRecord?.score,
                    recordDuration: lastGameRecord?.durationInMs
                      ? getTimeString(
                          lastGameRecord?.durationInMs,
                          'HH:mm:ss.SS',
                          true,
                        )
                      : undefined,
                    isUnlocked: index <= newestUnlockedIndex,
                    aiRevisionStatus: undefined,
                    hasProgressArrow: true,
                    onClickPlay: () => {
                      // const scrollPosition =
                      //   window.scrollY || window.pageYOffset;
                      // console.log(indexForChallenge);
                      // console.log(index === newestUnlockedIndex ? -1 : index);

                      if (state.isForChallengeGameOnly) {
                        useGameStore
                          .getState()
                          .setAlbumScrollPosition(
                            index === newestUnlockedIndexForChallenge
                              ? -1
                              : indexForChallenge,
                          );
                      } else {
                        useGameStore
                          .getState()
                          .setAlbumScrollPosition(
                            index === newestUnlockedIndex ? -1 : index,
                          );
                      }
                      useRecordStore
                        .getState()
                        .moveInAlbumSummaryPath(
                          classroom.classroomId,
                          album.gameSetId,
                          index,
                        )
                        .catch(() => {
                          console.error();
                        });
                      navigate(
                        `/club/${classroom.classroomId}/journey/${album.gameSetId}/game/${game.gameId}`,
                      );
                    },
                    onClickInfo: () => {
                      const popupInfoType = stringToGameTypeHelpType(
                        gameItem?.icon?.tag,
                      );
                      popupInfoType && onClickInfo(popupInfoType);
                    },
                    onClickAnalysis: () => {
                      navigate(
                        `/club/${classroom.classroomId}/journey/${album.gameSetId}/game/${game.gameId}/analysis`,
                      );
                    },
                    onClickShare: () => {
                      onShareGame(
                        `/club/${classroom.classroomId}/journey/${album.gameSetId}/game/${game.gameId}/share`,
                      );
                    },
                  },
                  isCompleted: !!canPassStation,
                  isForChallengeGame: !!gameItem?.game?.isForChallenge,
                };
              }
              return {
                type: StationCellType.Empty,
                props: {
                  stationId:
                    stationStyle?.stations.empty ?? StationEmptyItemNo.s1,
                  name: name,
                  isUnlocked: index <= newestUnlockedIndex,
                },
                isCompleted: true,
                isForChallengeGame: false,
              };
            }
            case StationType.AssistedGame: {
              const gameItem = gameItems?.[currentAssistedGameIndex];
              const game = games?.find(
                (g) => g?.gameId === gameItem?.game?.gameId,
              );
              const gameStatistics = allGameStatistics.find(
                (stats) => stats.gameId === gameItem?.game?.gameId,
              );
              const gameRecords = allGameRecords.filter(
                (record) =>
                  record.gameId === gameItem?.game?.gameId && record.isAssisted,
              );
              const lastGameRecord = gameRecords.find(
                (record) => record.completed,
              );
              currentAssistedGameIndex = currentAssistedGameIndex + 1;
              const canPassStation = gameRecords?.find(
                (record) => record.canPassStation,
              );

              const prefixCode =
                // gameItem?.icon?.tag === 'syllable' ||
                // gameItem?.icon?.tag === 'fingerWritingStrokeByComponent'
                gameItem?.game?.isForChallenge
                  ? `L${currentStandardGameIndex + 1}`
                  : `L${currentStandardGameIndex + 1}.1`;

              if (game) {
                return {
                  type: StationCellType.Assisted,
                  props: {
                    game: game,
                    stationId:
                      stationStyle?.stations.assistedGame ??
                      StationTutorGameItemNo.s1,
                    name: name,
                    index: index,
                    iconKey: gameItem?.icon?.tag ?? '',
                    prefixCode: prefixCode,
                    playCount:
                      (gameStatistics?.statistics.angelPlayCount ?? 0) +
                      (gameStatistics?.statistics.assistedPlayCount ?? 0),
                    userProfile: userProfile,
                    recordScore: lastGameRecord?.score,
                    recordDuration: lastGameRecord?.durationInMs
                      ? getTimeString(
                          lastGameRecord?.durationInMs,
                          'HH:mm:ss.SS',
                          true,
                        )
                      : undefined,
                    isUnlocked: index <= newestUnlockedIndex,
                    onClickPlay: () => {
                      // const scrollPosition =
                      //   window.scrollY || window.pageYOffset;
                      if (activeUserId !== null) {
                        useGameStore
                          .getState()
                          .setAlbumScrollPosition(
                            index === newestUnlockedIndex ? -1 : index,
                          );
                        useRecordStore
                          .getState()
                          .moveInAlbumSummaryPath(
                            classroom.classroomId,
                            album.gameSetId,
                            index,
                          )
                          .catch(() => {
                            console.error();
                          });
                        navigate(
                          `/club/${classroom.classroomId}/journey/${album.gameSetId}/game/${game.gameId}?mode=tutorial`,
                        );
                      } else {
                        setSharedData({
                          pathName: location.pathname,
                        });
                        navigate(
                          getWebPageLink(
                            WebPageType.JOYAJOY,
                            WebPageLink.Entry,
                          ),
                        );
                      }
                    },
                    onClickInfo: () => {
                      const popupInfoType =
                        stringToGameTypeHelpType('assisted');
                      popupInfoType && onClickInfo(popupInfoType);
                    },
                    onClickAnalysis: () => {
                      navigate(
                        `/club/${classroom.classroomId}/journey/${album.gameSetId}/game/${game.gameId}/analysis?mode=tutorial`,
                      );
                    },
                    onClickShare: () => {
                      onShareGame(
                        `/club/${classroom.classroomId}/journey/${album.gameSetId}/game/${game.gameId}/share?mode=tutorial`,
                      );
                    },
                  },
                  isCompleted: !!canPassStation,
                  isForChallengeGame: !!gameItem?.game?.isForChallenge,
                };
              }
              return {
                type: StationCellType.Empty,
                props: {
                  stationId:
                    stationStyle?.stations.empty ?? StationEmptyItemNo.s1,
                  name: name,
                  isUnlocked: index <= newestUnlockedIndex,
                },
                isCompleted: true,
                isForChallengeGame: false,
              };
            }
            case StationType.PathChallenge: {
              const partId: PartKey = String(challengeGameIndex).padStart(
                2,
                '0',
              ) as PartKey;
              _indexForChallenge++;

              const indexForChallenge = _indexForChallenge;

              // TODO: check the average score will be affected

              const angelPlayCount =
                album.statistics[`angelPathChallenge_${partId}PlayCount`] ?? 0;

              const playCount =
                (album.statistics[`pathChallenge_${partId}PlayCount`] ?? 0) +
                angelPlayCount;

              const scoreTotal =
                (album.statistics[`pathChallenge_${partId}ScoreTotal`] ?? 0) +
                angelPlayCount * hashForPlayCount(angelPlayCount); // random score for angel play count

              const averageScore = playCount ? scoreTotal / playCount : 0;
              const challengeGameRecords = allChallengeRecords.filter(
                (record) => record.type === `pathChallenge_${partId}`,
              );
              const lastChallengeGameRecord = challengeGameRecords.find(
                (record) => record.completed,
              );
              const remainingReward = computeReward(
                classroomMembership?.isAtLeastMember,
                { breadSlice: 30 },
                false,
                5,
                challengeGameRecords?.map((record) => record.reward) ?? [],
                1,
              ).reward;
              challengeGameIndex = challengeGameIndex + 1;
              const canPassStation = challengeGameRecords?.find(
                (record) => record.canPassStation,
              );
              return {
                type: StationCellType.Challenge,
                props: {
                  stationId:
                    stationStyle?.stations.miniChallenge ??
                    StationMiniChallengeItemNo.s1,
                  name: name,
                  index: index,
                  playCount: playCount,
                  averageScore: averageScore,
                  userProfile: userProfile,
                  remainingBreadReward: remainingReward.breadSlice,
                  recordScore: lastChallengeGameRecord?.score,
                  recordDuration: lastChallengeGameRecord?.durationInMs
                    ? getTimeString(
                        lastChallengeGameRecord?.durationInMs,
                        'HH:mm:ss.SS',
                        true,
                      )
                    : undefined,
                  isMiniChallenge: true,
                  isUnlocked: index <= newestUnlockedIndex,
                  onClickPlay: () => {
                    // const scrollPosition = window.scrollY || window.pageYOffset;
                    if (state.isForChallengeGameOnly) {
                      useGameStore
                        .getState()
                        .setAlbumScrollPosition(
                          index === newestUnlockedIndexForChallenge
                            ? -1
                            : indexForChallenge,
                        );
                    } else {
                      useGameStore
                        .getState()
                        .setAlbumScrollPosition(
                          index === newestUnlockedIndex ? -1 : index,
                        );
                    }
                    useRecordStore
                      .getState()
                      .moveInAlbumSummaryPath(
                        classroom.classroomId,
                        album.gameSetId,
                        index,
                      )
                      .catch(() => {
                        console.error();
                      });
                    navigate(
                      `/club/${classroom.classroomId}/journey/${album.gameSetId}/challenge/${partId}`,
                    );
                  },
                  onClickShare: () => {
                    onShareGame(
                      `/club/${classroom.classroomId}/journey/${album.gameSetId}/challenge/${partId}/share`,
                    );
                  },
                },
                isCompleted: !!canPassStation,
                isForChallengeGame: false,
              };
            }
            case StationType.SuperChallenge: {
              const playCount =
                (album.statistics.angelSuperChallengePlayCount ?? 0) +
                (album.statistics.superChallengePlayCount ?? 0);
              const scoreTotal =
                (album.statistics.superChallengeScoreTotal ?? 0) +
                (album.statistics.angelSuperChallengePlayCount ?? 0) *
                  hashForPlayCount(
                    album.statistics.angelSuperChallengePlayCount ?? 0,
                  );
              const averageScore = playCount ? scoreTotal / playCount : 0;
              const challengeGameRecords = allChallengeRecords.filter(
                (record) => record.type === `superChallenge`,
              );
              const lastChallengeGameRecord = challengeGameRecords.find(
                (record) => record.completed,
              );
              const canPassStation = challengeGameRecords?.find(
                (record) => record.canPassStation,
              );
              const remainingReward = computeReward(
                classroomMembership?.isAtLeastMember,
                { breadSlice: 50 },
                false,
                5,
                challengeGameRecords?.map((record) => record.reward) ?? [],
                1,
              ).reward;
              _indexForChallenge++;

              const indexForChallenge = _indexForChallenge;
              return {
                type: StationCellType.Challenge,
                props: {
                  stationId:
                    stationStyle?.stations.superChallenge ??
                    StationSuperChallengeItemNo.s1,
                  name: name,
                  index: index,
                  playCount: playCount,
                  averageScore: averageScore,
                  userProfile: userProfile,
                  remainingBreadReward: remainingReward.breadSlice,
                  recordScore: lastChallengeGameRecord?.score,
                  recordDuration: lastChallengeGameRecord?.durationInMs
                    ? getTimeString(
                        lastChallengeGameRecord?.durationInMs,
                        'HH:mm:ss.SS',
                        true,
                      )
                    : undefined,
                  isMiniChallenge: false,
                  isUnlocked: index <= newestUnlockedIndex,
                  onClickPlay: () => {
                    // const scrollPosition = window.scrollY || window.pageYOffset;
                    if (state.isForChallengeGameOnly) {
                      useGameStore
                        .getState()
                        .setAlbumScrollPosition(
                          index === newestUnlockedIndexForChallenge
                            ? -1
                            : indexForChallenge,
                        );
                    } else {
                      useGameStore
                        .getState()
                        .setAlbumScrollPosition(
                          index === newestUnlockedIndex ? -1 : index,
                        );
                    }
                    useRecordStore
                      .getState()
                      .moveInAlbumSummaryPath(
                        classroom.classroomId,
                        album.gameSetId,
                        index,
                      )
                      .catch(() => {
                        console.error();
                      });
                    navigate(
                      `/club/${classroom.classroomId}/journey/${album.gameSetId}/challenge/super`,
                    );
                  },
                  onClickShare: () => {
                    onShareGame(
                      `/club/${classroom.classroomId}/journey/${album.gameSetId}/challenge/super/share`,
                    );
                  },
                },
                isCompleted: !!canPassStation,
                isForChallengeGame: false,
              };
            }
            case StationType.Reward: {
              const isCollectedReward =
                !!albumSummary?.path?.collectedJoyBallAt;
              return {
                type: StationCellType.Reward,
                props: {
                  stationId:
                    stationStyle?.stations.reward ?? StationRewardItemNo.s1,
                  state: isCollectedReward
                    ? RewardStationCellState.CollectedRewards
                    : RewardStationCellState.Uncompleted,
                  name: name,
                  isUnlocked: index <= newestUnlockedIndex,
                  onClickPlay: () => {},
                },
                isCompleted: isCollectedReward,
                isForChallengeGame: false,
              };
            }
            case StationType.Goal: {
              const isCollectedReward =
                !!albumSummary?.path?.collectedStarPointAt;
              return {
                type: StationCellType.Goal,
                props: {
                  stationId:
                    stationStyle?.stations.goal ?? StationGoalItemNo.s1,
                  name: name,
                  isUnlocked: index <= newestUnlockedIndex,
                  userProfile: userProfile,
                  onClickPlay: () => {},
                },
                isCompleted: isCollectedReward,
                isForChallengeGame: false,
              };
            }
            default: {
              return {
                type: StationCellType.Empty,
                props: {
                  stationId:
                    stationStyle?.stations.empty ?? StationEmptyItemNo.s1,
                  name: name,
                  isUnlocked: index <= newestUnlockedIndex,
                },
                isCompleted: true,
                isForChallengeGame: false,
              };
            }
          }
        });
      }
    }
    return [];
  }, [
    album,
    classroom,
    userProfile,
    games,
    gameItems,
    allGameRecords,
    allChallengeRecords,
    albumSummary?.path?.viewedStartVideoAt,
    albumSummary?.path?.collectedJoyBallAt,
    albumSummary?.path?.collectedStarPointAt,
    startVideoThumbnailUrl,
    onClickAboutJourney,
    onClickVideo,
    allGameStatistics,
    classroomMembership?.isAtLeastMember,
    state.isForChallengeGameOnly,
    navigate,
    onClickInfo,
    onShareGame,
    activeUserId,
    setSharedData,
    location.pathname,
    hashForPlayCount,
  ]);

  const stations: PathStationCellProps[] = useMemo<StationCellProps[]>(() => {
    return stationsData
      .filter((station) => station.type !== StationCellType.Empty)
      .filter(
        (station) =>
          (station.isForChallengeGame &&
            station.type === StationCellType.Game) ||
          !state.isForChallengeGameOnly ||
          station.type === StationCellType.Start ||
          station.type === StationCellType.Challenge ||
          station.type === StationCellType.Goal,
      )
      .map((_station, index, arr) => {
        const station = cloneDeep(_station);
        if (index > 0) {
          const stationData = arr[index - 1];
          if (stationData) {
            station.props.isUnlocked =
              station.props.isUnlocked || !!stationData.isCompleted;
            if (!station.props.isUnlocked) {
              // if (station.type === StationCellType.Goal) {
              //   station.props.onClickPlay = () => {
              //     toast(
              //       t('Please complete all games first.'),
              //       AppToastInfoOption,
              //     );
              //   };
              if (
                [
                  StationCellType.Assisted,
                  StationCellType.Game,
                  StationCellType.Challenge,
                  StationCellType.Reward,
                  StationCellType.Goal,
                ].includes(station.type)
              ) {
                if (
                  [
                    StationCellType.Game,
                    StationCellType.Challenge,
                    StationCellType.Goal,
                  ].includes(stationData.type)
                ) {
                  station.props.onClickPlay = () => {
                    toast(
                      t(
                        'Complete the previous station and achieve good results to unlock this Station.',
                      ),
                      AppToastInfoOption,
                    );
                  };
                } else {
                  station.props.onClickPlay = () => {
                    toast(
                      t(
                        'Complete the previous station to unlock this Station.',
                      ),
                      AppToastInfoOption,
                    );
                  };
                }
              } else {
                // MARK: needed for block access
                station.props.onClickPlay = () => {};
              }
            }
            if (station.type === StationCellType.Reward) {
              if (station.props.state === RewardStationCellState.Uncompleted) {
                station.props.state = stationData.isCompleted
                  ? RewardStationCellState.AbleGetReward
                  : RewardStationCellState.Uncompleted;
              }
            }
          }
        }
        return station;
      })
      .reverse();
  }, [state.isForChallengeGameOnly, stationsData, t]);

  const nextStandardGame = () => {
    const isStandardGame = (item: PathStationCellProps) =>
      item.type === StationCellType.Game && item.isForChallengeGame;
    // item.props.iconKey !== 'syllable' &&
    // item.props.iconKey !== 'fingerWritingStrokeByComponent';

    const station = [...stations]
      .reverse()
      .find(
        (item) =>
          item.type === StationCellType.Game &&
          item.isCompleted === false &&
          isStandardGame(item),
      );
    const completedStandardGameCount = stations.filter(
      (item) => isStandardGame(item) && item.isCompleted === true,
    ).length;

    const totalStandardGameCount = stations.filter((item) =>
      isStandardGame(item),
    ).length;

    const isAllCompleted = completedStandardGameCount >= totalStandardGameCount;

    const gameLinkList = stations
      .filter(
        (item) => isStandardGame(item) && (isAllCompleted || !item.isCompleted),
      )
      .map(
        (item) =>
          `/club/${album?.classroomId}/journey/${album?.gameSetId}/game/${
            item.type === StationCellType.Game ? item.props.game.gameId : ''
          }`,
      );

    return {
      index:
        station?.type === StationCellType.Game ? station.props.index : null,
      classroomId:
        station?.type === StationCellType.Game ? album?.classroomId : '',
      gameSetId: album?.gameSetId ?? '',
      gameId:
        station?.type === StationCellType.Game ? station.props.game.gameId : '',
      completedStandardGameCount,
      totalStandardGameCount,
      gameLinkList: isAllCompleted
        ? shuffle(gameLinkList)
        : gameLinkList.reverse(),
    };
  };

  const onClickNextStandardGame = () => {
    const {
      index,
      completedStandardGameCount,
      totalStandardGameCount,
      gameLinkList,
      classroomId: _classroomId,
      gameSetId: _gameSetId,
      gameId: _gameId,
    } = nextStandardGame();

    if (!activeUserId) {
      setSharedData({
        pathName: location.pathname,
      });
      navigate(getWebPageLink(WebPageType.JOYAJOY, WebPageLink.Entry));
    } else if (completedStandardGameCount < totalStandardGameCount) {
      if (index) {
        useRecordStore
          .getState()
          .moveInAlbumSummaryPath(classroomId ?? '', gameSetId ?? '', index)
          .catch(() => {
            console.error();
          });
      }

      useSessionStore.getState().setGameListsData({
        type: GameListsType.Standard,
        gameLinkList: gameLinkList.slice(1),
        totalGames: totalStandardGameCount,
      });
      navigate(`/club/${_classroomId}/journey/${_gameSetId}/game/${_gameId}`);
    } else {
      const firstGame = gameLinkList[0];
      if (firstGame) {
        const newGameLinkList = gameLinkList.slice(1);
        useSessionStore.getState().setGameListsData({
          type: GameListsType.Standard,
          gameLinkList: newGameLinkList,
          totalGames: totalStandardGameCount,
        });
        navigate(firstGame);
      } else {
        scrollToGivenIndexStation(false, true, newestUnlockedStationIndex);
      }
    }
  };

  // console.log(stations);

  const newestUnlockedStation = useMemo(() => {
    return stations.find(
      (station) =>
        station.props.isUnlocked &&
        (station.type === StationCellType.Game ||
          station.type === StationCellType.Assisted ||
          station.type === StationCellType.Goal ||
          station.type === StationCellType.Challenge ||
          station.type === StationCellType.Reward),
    );
  }, [stations]);

  const newestUnlockedStationIndex = useMemo(() => {
    if (!state.isStationsUpdated) return -1;

    if (newestUnlockedStation) {
      return stations.indexOf(newestUnlockedStation);
    } else {
      return -1;
    }
  }, [newestUnlockedStation, stations, state.isStationsUpdated]);

  // console.log('newest unlocked:', newestUnlockedStationIndex);

  const recentStationIndex = useMemo(() => {
    const currentStationIndex = albumSummary?.path?.currentStation;
    if (
      state.isForChallengeGameOnly &&
      currentStationIndex &&
      currentStationIndex > -1
    ) {
      const stationData = stationsData[currentStationIndex];
      const stationIndex = stations.findIndex((station) =>
        isEqual(station, stationData),
      );
      if (stationIndex > -1) {
        return stations.length - 1 - stationIndex;
      }
      return stations.length - 1 - newestUnlockedStationIndex;
    }
    return currentStationIndex;
  }, [
    albumSummary?.path?.currentStation,
    newestUnlockedStationIndex,
    state.isForChallengeGameOnly,
    stations,
    stationsData,
  ]);

  const reversedCurrentStationIndex = useMemo(() => {
    const recentIndex = recentStationIndex;

    if (recentIndex !== undefined) {
      return stations.length - 1 - recentIndex;
    }
    return -1;
  }, [recentStationIndex, stations.length]);

  // const scrollToBottom = useCallback(() => {
  //   setWhichPopup(whichPopup.nothing);
  //   document.documentElement.style.scrollBehavior = 'auto';
  //   document.body.style.scrollBehavior = 'auto';
  //   document.documentElement.scrollTop = document.documentElement.scrollHeight;
  //   document.body.scrollTop = document.documentElement.scrollHeight;
  // }, []);

  const scrollToGivenIndexStation = useCallback(
    (isPlay: boolean, smoothScroll: boolean, scrollIndex: number) => {
      // console.log('scrolled to', scrollIndex);
      let givenStation: PathStationCellProps | undefined;
      if (scrollIndex === -1) {
        givenStation = stations[newestUnlockedStationIndex];
      } else {
        givenStation = stations[scrollIndex];
      }
      let stationIndex;
      if (givenStation) {
        stationIndex = stations.indexOf(givenStation);
      } else {
        stationIndex = 0;
      }

      const pathListView = document.querySelector('.path-list-view');
      if (pathListView) {
        const stationElements = pathListView.children;
        const stationElement = stationElements[stationIndex];

        if (stationElement) {
          const elementRect = stationElement.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const middle =
            absoluteElementTop -
            window.innerHeight / 2 +
            elementRect.height / 2;
          if (smoothScroll) {
            window.scrollTo({
              top: middle,
              behavior: 'smooth',
            });
          } else {
            document.documentElement.style.scrollBehavior = 'auto';
            document.body.style.scrollBehavior = 'auto';
            document.documentElement.scrollTop = middle;
            document.body.scrollTop = middle;
          }
          if (isPlay) {
            setTimeout(() => {
              if (givenStation?.props.onClickPlay) {
                givenStation.props.onClickPlay();
              }
            }, 1000);
          }
        }
      }
      // }
    },
    [newestUnlockedStationIndex, stations],
  );

  useEffect(() => {
    let position = useGameStore.getState().getAlbumScrollPosition();
    position =
      position === -1 || position === null
        ? position
        : stations.length - position - 1;
    if (position === -1 && !state.isLoading) {
      // pos = -1, special blinking case
      // setState((s) => ({ ...s, isLoading: true }));
      setTimeout(() => {
        // setState((s) => ({ ...s, isLoading: false }));
        scrollToGivenIndexStation(false, true, position ?? -1);
        if (newestUnlockedStationIndex !== -1) {
          setState((s) => ({
            ...s,
            blinkingStationIndex: newestUnlockedStationIndex,
          }));
          setTimeout(() => {
            setState((s) => ({ ...s, blinkingStationIndex: undefined }));
          }, 6000);
          useGameStore.getState().setAlbumScrollPosition(null);
        }
      }, 500);
    } else if (position !== null && !state.isLoading) {
      // hv pos, scroll to the given position
      scrollToGivenIndexStation(false, true, position);
      useGameStore.getState().setAlbumScrollPosition(null);
    } else if (!state.isLoading) {
      // no pos, entered from outside, scroll to newest station
      setTimeout(() => {
        scrollToGivenIndexStation(
          false,
          false,
          reversedCurrentStationIndex ?? -1,
        );
      }, 200);
      if (stations.length - newestUnlockedStationIndex - 1 === 1) {
        setState((s) => ({
          ...s,
          blinkingStationIndex: newestUnlockedStationIndex,
        }));
        setTimeout(() => {
          setState((s) => ({ ...s, blinkingStationIndex: undefined }));
        }, 6000);
      }
      useGameStore.getState().setAlbumScrollPosition(null);
    }
  }, [
    newestUnlockedStationIndex,
    reversedCurrentStationIndex,
    scrollToGivenIndexStation,
    state.isLoading,
    stations.length,
  ]);

  const onPressStart = useCallback(() => {
    // if (getAlbumPlayCount(album?.statistics) || 0 > 0) {
    setState((s) => ({ ...s, popupState: undefined }));
    if (startVideoThumbnailUrl) {
      onClickVideo();
    } else {
      scrollToGivenIndexStation(true, true, stations.length - 2);
    }

    // scrollToBottom;
  }, [
    stations.length,
    onClickVideo,
    scrollToGivenIndexStation,
    startVideoThumbnailUrl,
  ]);

  const onClickChallengeAll = useCallback(() => {
    if (classroom && album) {
      navigate(
        `/club/${classroom.classroomId}/journey/${album.gameSetId}/challenge/comprehensive`,
      );
    }
  }, [album, classroom, navigate]);

  const onSwitchForChallengeOnly = useCallback(
    (isForChallengeGameOnly: boolean) => {
      setState((s) => ({
        ...s,
        isForChallengeGameOnly: isForChallengeGameOnly,
      }));
      useRecordStore
        .getState()
        .updateAlbumSummaryPath(classroomId ?? '', gameSetId ?? '', {
          showGameForChallengeOnly: isForChallengeGameOnly,
        })
        .catch(() => {
          console.error();
        });
      window.scrollTo(0, document.body.scrollHeight);
    },
    [classroomId, gameSetId],
  );

  const isGoal = useMemo(() => {
    const recentStation = albumSummary?.path?.currentStation ?? -1;
    // console.log('recentStation', albumSummary?.path?.currentStation);
    // const challengeGameRecords =
    //   allChallengeRecords?.filter(
    //     (record) => record?.type === `superChallenge`,
    //   ) ?? [];

    const lastChallenge = allChallengeRecords[0];
    const lastGameRecord = allGameRecords[0];
    const canPassStation = lastChallenge?.canPassStation;
    const isChallenge = lastChallenge?.type === `superChallenge`;
    const isMostRecent =
      (lastChallenge?.endedAt ?? 0) >= (lastGameRecord?.endedAt ?? 0);
    // console.log(lastChallenge);
    // if (state.isForChallengeGameOnly) {
    //   return canPassStation && recentStation === 1;
    // }
    return (
      canPassStation &&
      isChallenge &&
      isMostRecent &&
      stationsData.length - recentStation === 2
    );
  }, [
    albumSummary?.path?.currentStation,
    allChallengeRecords,
    allGameRecords,
    stationsData.length,
  ]);

  return (
    <>
      {state.isLoading ? <LoadingSpinner loading={state.isLoading} /> : null}
      <AlbumPathListTemplate
        currentUserProfile={userProfile}
        classroom={classroom}
        albumName={albumName}
        pathStyleBackground={album?.content?.path[0]?.style.background.set ?? 0} // TODO: may also assign image
        stations={stations}
        blinkingStationIndex={state.blinkingStationIndex}
        recentStationIndex={recentStationIndex}
        completedStandardGameCount={
          nextStandardGame().completedStandardGameCount
        }
        totalStandardGameCount={nextStandardGame().totalStandardGameCount}
        overallScore={
          albumSummary?.parts.overall.averageGameScore
            ? numbro(albumSummary?.parts.overall.averageGameScore).format({
                mantissa: 2,
                trimMantissa: true,
              })
            : undefined
        }
        totalRecordDuration={
          albumSummary?.parts.overall.totalDurationInMs
            ? getTimeString(
                albumSummary?.parts.overall.totalDurationInMs,
                'HH:mm:ss.SS',
                true,
              )
            : undefined
        }
        isForChallengeGameOnly={state.isForChallengeGameOnly}
        onClickClassroom={onClickClassroom}
        onClickAboutJourney={onClickAboutJourney}
        onClickChallengeAll={onClickChallengeAll}
        onClickSharePath={onSharePath}
        onClickAlbumOptions={
          classroomMembership?.isRoot ? onClickAlbumOptions : undefined
        }
        onScrollToCurrent={() =>
          scrollToGivenIndexStation(false, true, reversedCurrentStationIndex)
        }
        onClickPlayNextStandardGame={onClickNextStandardGame}
        onSwitchForChallengeOnly={onSwitchForChallengeOnly}
        isGoal={isGoal ?? false}
      />

      {state.popupState === PopupState.About &&
      userProfile &&
      classroom &&
      album ? (
        <AboutJourneySheetPopup
          profile={userProfile}
          album={album}
          playCount={album?.playCount ?? 0}
          albumClassroom={classroom}
          startVideoThumbnailUrl={startVideoThumbnailUrl}
          onBack={() => {
            setState((s) => ({ ...s, popupState: undefined }));
          }}
          onPressInfo={() => {
            setState((s) => ({ ...s, popupState: PopupState.MemoryBoost }));
          }}
          onPressStart={onPressStart}
        />
      ) : null}

      {state.popupState === PopupState.MemoryBoost ? (
        <MemoryBoostInfoModal
          onClose={() => {
            setState((s) => ({ ...s, popupState: PopupState.About }));
          }}
        />
      ) : null}
    </>
  );
};

export default AlbumPathListScreen;
